import { CenterItem, Column, RowAlignedItems } from '@components/LinearLayout';
import {
  InputFieldsType,
  designTypeFields,
  orderedFields,
} from '@composites/InboundButton/utils';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';

import Button from '@components/Button';
import WeModal from '@components/WeModal';
import { ApplyButtonWrapper } from '@composites/Filters/styles';
import { DefaultBooleanValues } from '@composites/InboundButton';
import { getEventDetails } from '@store/slices/eventSlice';
import { SPEAKER_PAGE_WIDTH } from '@utils/constants';
import { safeJSONParse } from '@utils/fn';
import { useInbound } from '@utils/Inbound/useInbound';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player/lazy';
import RegistrationForm from '../RegistrationForm';

const VideoContainer = styled.div`
  position: relative;
  height: auto;
  padding-top: 56.25%;
`;
const VideoPlayer = styled(ReactPlayer)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;
interface VideoContentProps {
  label: string;
  buttonType: string;
  eventId?: string;
  inboundValues?: {
    nonBooleanValues: Record<string, string>;
    booleanValues: DefaultBooleanValues;
  };
}

const VideoContent = ({
  label,
  inboundValues,
  buttonType,
  eventId,
}: VideoContentProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const [showModal, setShowModal] = useState<boolean>(false);
  const [checkedApproval, setCheckedApproval] = useState<boolean>(false);

  const { inboundCookie, actionInbound, registerInbound, shouldShowForm } =
    useInbound();

  const shouldShowPopup = useMemo(() => {
    return (
      inboundValues?.nonBooleanValues?.['show-popup'] === 'true' &&
      !inboundCookie
    );
  }, [inboundCookie, inboundValues?.nonBooleanValues]);

  const handlePlay = useCallback(async () => {
    if (!inboundCookie) {
      setShowModal(true);
    } else {
      await actionInbound({
        ...JSON.parse(inboundCookie ?? '{}'),
        event: sluggedEvent?.id,
        path: window.location.href,
        action: 'video_content',
      });
    }
  }, [inboundCookie, sluggedEvent?.id, sluggedEvent?.portfolio?.id]);

  const inputElements = useMemo(
    () =>
      orderedFields
        .map(item => {
          if (inboundValues?.booleanValues?.[item]?.visible) {
            const designTypeParameters = designTypeFields?.find(
              e => e.id === item
            );
            return {
              ...designTypeParameters,
              ...inboundValues?.booleanValues?.[item],
            };
          }
          return undefined;
        })
        ?.filter(item => item) as InputFieldsType,
    [inboundValues?.booleanValues]
  );

  const defaultValues: Record<string, string> = useMemo(() => {
    return inputElements.reduce<Record<string, string>>((acc, key) => {
      acc[key.id] = '';
      return acc;
    }, {});
  }, [inputElements]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  // Clear error for a field when user starts typing in it
  const handleChange = (fieldName: string) => (value: string) => {
    clearErrors(fieldName);
  };

  const onSubmit = useCallback(
    async (data: Record<string, string>) => {
      if (!checkedApproval) return;

      await registerInbound(
        {
          ...data,
          event: sluggedEvent?.id,
          path: window.location.href,
          action: 'video_content',
        },
        () => {
          setShowModal(false);
        }
      );
    },
    [checkedApproval, registerInbound, inboundCookie, sluggedEvent?.id]
  );

  return (
    <div>
      {!shouldShowForm ? (
        <VideoContainer>
          <VideoPlayer
            width={inboundValues?.nonBooleanValues?.['video-width']}
            height="100%"
            muted={false}
            loop
            playsinline
            pip
            controls
            onPlay={handlePlay}
            url={inboundValues?.nonBooleanValues?.['video-link']}
            config={{
              youtube: {
                playerVars: {
                  enablejsapi: 1,
                  origin: window.location.origin,
                },
              },
            }}
          />
        </VideoContainer>
      ) : shouldShowPopup ? (
        <Button
          title={label}
          backgroundColor={sluggedEvent?.design?.themeColor}
          hoverBackgroundColor={theme.color.mobilityGrey}
          textColor={theme.color.white}
          lineHeight={theme.lineHeight.height125}
          fontSize={theme.fontSize.size20}
          borderRadius={theme.borderRadius.radius10}
          border={`0.1rem solid ${theme.color.mobilityGrey}`}
          padding="1.1rem 3rem"
          width="max-content"
          onClick={() => {
            setShowModal(true);
          }}
        />
      ) : (
        <Column>
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <RegistrationForm
              control={control}
              errors={errors}
              inputElements={inputElements}
              setCheckedApproval={setCheckedApproval}
              checkedApproval={checkedApproval}
              handleChange={handleChange}
            />
          </form>
          <Button
            title={label}
            backgroundColor={sluggedEvent?.design.themeColor}
            hoverBackgroundColor={theme.color.mobilityGrey}
            textColor={theme.color.white}
            borderRadius={theme.borderRadius.radius10}
            padding="0.6rem 2.5rem"
            width="max-content"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmit(onSubmit)}
          />
        </Column>
      )}
      <WeModal
        visible={showModal}
        maxHeight="94vh"
        backgroundColor={theme.color.pureWhite}
        width={`min(${SPEAKER_PAGE_WIDTH}, 90vw)`}
        onClose={() => {
          setShowModal(false);
        }}
        footer={
          <CenterItem>
            <ApplyButtonWrapper>
              <RowAlignedItems $gap="3rem">
                <Button
                  title={t('save')}
                  backgroundColor={sluggedEvent?.design.themeColor}
                  hoverBackgroundColor={theme.color.mobilityGrey}
                  textColor={theme.color.white}
                  borderRadius={theme.borderRadius.radius10}
                  padding="0.6rem 2.5rem"
                  width="max-content"
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={handleSubmit(onSubmit)}
                />
              </RowAlignedItems>
            </ApplyButtonWrapper>
          </CenterItem>
        }
      >
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <RegistrationForm
            control={control}
            errors={errors}
            inputElements={inputElements}
            setCheckedApproval={setCheckedApproval}
            checkedApproval={checkedApproval}
            handleChange={handleChange}
          />
        </form>
      </WeModal>
    </div>
  );
};

export default VideoContent;
